import Tool, {
  type ToolArgsWithController,
  type ToolArgs,
} from '../../../tools/Tool';
import { svgToIcon } from '../../../components/Toolbar/toolbarUtils';
import TableUtils from '../TableUtils';
import insertColumnAfterSvg from '../../../assets/icons/insertColumnAfter.svg';
import insertColumnBeforeSvg from '../../../assets/icons/insertColumnBefore.svg';
import insertRowAfterSvg from '../../../assets/icons/insertRowAfter.svg';
import insertRowBeforeSvg from '../../../assets/icons/insertRowBefore.svg';
import type AhaEditorV2Controller from '../../../controllers/AhaEditorV2Controller';

function selectedRowCount(controller: AhaEditorV2Controller): number {
  const { tableSelection } = controller?.state.pluginState;
  if (tableSelection?.multipleCellsSelected) {
    return tableSelection.height;
  }

  return 1;
}

function selectedColumnCount(controller: AhaEditorV2Controller): number {
  const { tableSelection } = controller?.state.pluginState;

  if (tableSelection?.multipleCellsSelected) {
    return tableSelection.width;
  }

  return 1;
}

class ToolInsertRowsOrColumns extends Tool {
  static toolName = 'Add rows or columns';
  static iconClass = 'fa-regular fa-plus-large';
  static submenuTools = () => [
    [
      ToolInsertRowBefore,
      ToolInsertRowAfter,
      ToolInsertColumnAfter,
      ToolInsertColumnBefore,
    ],
  ];

  static isEnabled({ editor, controller }: ToolArgsWithController) {
    return Boolean(
      selectedRowCount(controller) > 1 ||
        selectedColumnCount(controller) > 1 ||
        TableUtils.canInsertOrDeleteRowsOrColumns(editor)
    );
  }
}

class ToolInsertColumnAfter extends Tool {
  static toolName = 'Insert column after';

  static tooltip({ editor, controller }: ToolArgsWithController): string {
    const count = selectedColumnCount(controller);
    if (count > 1) {
      return `Insert ${count} columns after`;
    }
    return super.tooltip({ editor, controller });
  }

  static renderToolbarButtonIcon({ editor }: ToolArgs): JSX.Element {
    return svgToIcon(insertColumnAfterSvg);
  }

  static action({ editor, element, controller }: ToolArgs): void {
    if (!editor?.selection) return;
    const tableSelection = controller?.state.pluginState.tableSelection;

    let path = tableSelection?.endCellPath;

    if (!path) {
      path = TableUtils.currentCellPath(editor);
    }

    if (!path) return;

    for (let i = 0; i < selectedColumnCount(controller); i++) {
      TableUtils.insertColumn(editor, path);
    }
  }
}

class ToolInsertColumnBefore extends Tool {
  static toolName = 'Insert column before';

  static tooltip({ editor, controller }: ToolArgsWithController): string {
    const count = selectedColumnCount(controller);
    if (count > 1) {
      return `Insert ${count} columns before`;
    }
    return super.tooltip({ editor, controller });
  }

  static renderToolbarButtonIcon({ editor }: ToolArgs): JSX.Element {
    return svgToIcon(insertColumnBeforeSvg);
  }

  static action({ editor, element, controller }: ToolArgs): void {
    if (!editor?.selection) return;
    const tableSelection = controller?.state.pluginState.tableSelection;

    let path = tableSelection?.startCellPath;

    if (!path) {
      path = TableUtils.currentCellPath(editor);
    }

    if (!path) return;

    for (let i = 0; i < selectedColumnCount(controller); i++) {
      TableUtils.insertColumn(editor, path, true);
    }
  }
}

class ToolInsertRowAfter extends Tool {
  static toolName = 'Insert row after';

  static tooltip({ editor, controller }: ToolArgsWithController): string {
    const count = selectedRowCount(controller);
    if (count > 1) {
      return `Insert ${count} rows after`;
    }
    return super.tooltip({ editor, controller });
  }

  static renderToolbarButtonIcon({ editor }: ToolArgs): JSX.Element {
    return svgToIcon(insertRowAfterSvg);
  }

  static action({ editor, element, controller }: ToolArgs): void {
    if (!editor?.selection) return;
    const tableSelection = controller?.state.pluginState.tableSelection;

    let path = tableSelection?.endCellPath;

    if (!path) {
      path = TableUtils.currentCellPath(editor);
    }

    if (!path) return;

    for (let i = 0; i < selectedRowCount(controller); i++) {
      TableUtils.insertRow(editor, path);
    }
  }
}

class ToolInsertRowBefore extends Tool {
  static toolName = 'Insert row before';

  static tooltip({ editor, controller }: ToolArgsWithController): string {
    const count = selectedRowCount(controller);
    if (count > 1) {
      return `Insert ${count} rows before`;
    }
    return super.tooltip({ editor, controller });
  }

  static renderToolbarButtonIcon({ editor }: ToolArgs): JSX.Element {
    return svgToIcon(insertRowBeforeSvg);
  }

  static action({ editor, element, controller }: ToolArgs): void {
    if (!editor?.selection) return;
    const tableSelection = controller?.state.pluginState.tableSelection;

    let path = tableSelection?.startCellPath;

    if (!path) {
      path = TableUtils.currentCellPath(editor);
    }

    if (!path) return;

    for (let i = 0; i < selectedRowCount(controller); i++) {
      TableUtils.insertRow(editor, path, true);
    }
  }
}

export {
  ToolInsertRowsOrColumns,
  ToolInsertColumnAfter,
  ToolInsertColumnBefore,
  ToolInsertRowAfter,
  ToolInsertRowBefore,
};
